import React, { useState } from 'react'
import styled from 'styled-components'
import { Icon } from '../icon'

interface CheckboxProps {
    id: string
    checked?: boolean
    defaultChecked?: boolean
    label?: string
    onChange?: (
        checked: boolean,
        event: React.ChangeEvent<HTMLInputElement>,
    ) => void
    disabled?: boolean
}

export function Checkbox({
    id,
    checked,
    defaultChecked = false,
    onChange,
    disabled = false,
    label,
}: CheckboxProps) {
    const [internalChecked, setInternalChecked] = useState(defaultChecked)
    const isControlled = checked !== undefined
    const checkedValue = isControlled ? checked : internalChecked

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newChecked = event.target.checked
        if (!isControlled) {
            setInternalChecked(newChecked)
        }
        onChange?.(newChecked, event)
    }

    return (
        <Container>
            <Root
                data-state={checkedValue ? 'checked' : 'unchecked'}
                data-disabled={disabled ? '' : undefined}
            >
                <HiddenInput
                    id={id}
                    type="checkbox"
                    checked={checkedValue}
                    onChange={handleChange}
                    disabled={disabled}
                />
                <Indicator data-state={checkedValue ? 'checked' : 'unchecked'}>
                    <CheckIcon />
                </Indicator>
            </Root>
            {label && <Label htmlFor={id}>{label}</Label>}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const Root = styled.div`
    box-sizing: border-box;
    display: flex;
    width: 1.25rem;
    height: 1.25rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    outline: 0;
    padding: 0;
    margin: 0;
    border: none;
    position: relative;

    &[data-state='unchecked'] {
        border: 1px solid ${({ theme }) => theme.colors.greyScale[200]};
        background-color: transparent;
    }

    &[data-state='checked'] {
        background-color: ${({ theme }) => theme.colors.text.primary};
    }

    :focus-within {
        outline: 2px solid ${({ theme }) => theme.colors.info};
        outline-offset: 2px;
    }

    &[data-disabled] {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:not([data-disabled]) {
        cursor: pointer;
    }
`

const HiddenInput = styled.input`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    margin: 0;
`

const Indicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.inverse};

    &[data-state='unchecked'] {
        display: none;
    }
`

interface CheckIconProps extends React.SVGProps<SVGSVGElement> {}

function CheckIcon({ ...props }: CheckIconProps) {
    return (
        <Icon
            name="check"
            iconColor={({ theme }) => theme.colors.text.inverse}
            size="small"
        />
    )
}

const Label = styled.label`
    font-size: ${({ theme }) => theme.typography.fontSize['base']};
    font-weight: ${({ theme }) => theme.typography.fontWeight['medium']};
    margin-left: ${({ theme }) => theme.spacing[2]};
`
